import { unreachable } from "@mdotm/utils/runtime";
import type { PortfolioLabInvestmentItem } from "../../ctx";
import type { CompositionRowType, MergedCompositionRow, VirtualUuidData } from "./rows";
import type { CoveredHoldings } from "./weight-edit/editor";

/**
 * Single source of truth for composition-cell editability (T3).
 *
 * Both the AG Grid `editable` callbacks (`columns.tsx`) and the edit resolver
 * (`weight-edit/resolve.ts`) must route through `parseEditableCell`, so the grid can never
 * open an editor for a cell whose edit the resolver would then reject (or vice versa).
 *
 * Callers must pass LIVE investments — read via `readAtom(ctx.investments$)` at call time
 * (or the resolver's own input) — never a list captured when column defs were built: column
 * defs only rebuild on investment add/remove, so anything captured there goes stale after
 * Update-AUM / Reset (the old `hasAum` closure bug).
 */

export type CompositionCellEditabilityParams = {
	virtualUuid: string;
	row: MergedCompositionRow | undefined;
	investments: PortfolioLabInvestmentItem[];
};

/** A portfolio without an AUM loads with `DEFAULT_LAB_AUM` (0), so zero counts as no AUM. */
export function hasEditableAum(
	investment: PortfolioLabInvestmentItem,
): investment is PortfolioLabInvestmentItem & { aum: number } {
	return investment.aum != null && investment.aum !== 0;
}

export type EditableEntry = VirtualUuidData & { rowType: CompositionRowType };

export type EditableCell =
	| { unit: "weight"; investment: PortfolioLabInvestmentItem; covered: CoveredHoldings }
	| { unit: "monetary"; investment: PortfolioLabInvestmentItem & { aum: number }; covered: CoveredHoldings };

export type CompositionCellUnit = EditableCell["unit"];

/**
 * The cell's investment and the holdings its edit covers, when the cell is editable; a monetary cell
 * also needs an AUM.
 */
export function parseEditableCell(
	{ virtualUuid, row, investments }: CompositionCellEditabilityParams,
	unit: CompositionCellUnit,
): EditableCell | null {
	const investment = investments.find((i) => i.virtualUuid === virtualUuid);
	if (!investment || investment.compositionSaveStatus === "saving") {
		return null;
	}
	const entry = row?.data[virtualUuid];
	if (!hasRowType(entry)) {
		return null;
	}
	// Instruments nested inside a portfolio are read-only; every other row type is editable.
	if (entry.rowType === "instrument" && entry.portfolioUuid != null) {
		return null;
	}
	const covered = holdingsCoveredByRow(entry);
	if (!covered) {
		return null;
	}
	if (unit === "weight") {
		return { unit, investment, covered };
	}
	return hasEditableAum(investment) ? { unit, investment, covered } : null;
}

export function isWeightCellEditable(params: CompositionCellEditabilityParams): boolean {
	return parseEditableCell(params, "weight") != null;
}

export function isMonetaryCellEditable(params: CompositionCellEditabilityParams): boolean {
	return parseEditableCell(params, "monetary") != null;
}

function hasRowType(entry: VirtualUuidData | undefined): entry is EditableEntry {
	return entry?.rowType !== undefined;
}

function holdingsCoveredByRow(entry: EditableEntry): CoveredHoldings | null {
	const { rowType, tickerName, portfolioUuid, allocationLabel } = entry;
	switch (rowType) {
		case "instrument":
			return tickerName == null ? null : { kind: "instrument", tickerName };
		case "single-instruments":
			return { kind: "direct-instruments" };
		case "group":
			return allocationLabel == null ? null : { kind: "class", allocationLabel };
		case "portfolio":
		case "subgroup":
			if (portfolioUuid != null && allocationLabel != null) {
				return { kind: "portfolio-class", portfolioUuid, allocationLabel };
			}
			if (portfolioUuid != null) {
				return { kind: "portfolio", portfolioUuid };
			}
			if (allocationLabel != null) {
				return { kind: "direct-class", allocationLabel };
			}
			return null;
		default:
			return unreachable(rowType);
	}
}
